// components/ErrorBoundary.js
//
// The wall between one broken component and a blank white page.
//
// Two failure classes end here. A render throw anywhere below shows a small
// apology with a reload button instead of nothing (and reports to Sentry
// when a DSN is configured). And the deploy-staleness crash — a tab open
// across a Vercel deploy asks for a chunk that no longer exists — reloads
// itself ONCE, silently, because the fresh deploy is the fix; the
// sessionStorage latch stops a broken deploy from looping the reload.
import React from 'react';
import { reportError } from '../services/errorReporting';

const RELOAD_LATCH = 'aa-chunk-reloaded';

const isStaleChunk = (error) => {
  const s = `${error?.name || ''} ${error?.message || ''}`;
  return /ChunkLoadError|Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(s);
};

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { broken: false };
  }

  static getDerivedStateFromError() {
    return { broken: true };
  }

  componentDidCatch(error, info) {
    if (isStaleChunk(error) && !sessionStorage.getItem(RELOAD_LATCH)) {
      sessionStorage.setItem(RELOAD_LATCH, '1');
      window.location.reload();
      return;
    }
    reportError(error, { extra: { componentStack: info?.componentStack } });
  }

  render() {
    if (!this.state.broken) return this.props.children;
    return (
      <div style={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24,
        textAlign: 'center', fontFamily: 'inherit',
      }}>
        <span className="material-symbols-rounded" style={{ fontSize: '2.4rem', opacity: 0.6 }}>error</span>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Something broke on this screen.</h2>
        <p style={{ margin: 0, maxWidth: '44ch', opacity: 0.75, fontSize: '0.9rem' }}>
          Your data is fine — this is a display error, and it has been reported.
          Reloading usually clears it.
        </p>
        <button
          type="button"
          onClick={() => { sessionStorage.removeItem(RELOAD_LATCH); window.location.reload(); }}
          style={{
            border: 'none', borderRadius: 999, padding: '9px 22px', cursor: 'pointer',
            background: '#0D7F74', color: 'white', font: 'inherit', fontWeight: 600,
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
